import {
  toZonedTime,
  getDifference,
  dayjs,
  parseISOSafe,
  getMidnightInTimezone,
  getDayBoundsInTimezone,
  nullsToUndefined,
  type Nullable,
} from '@vitvio/helpers';
import { useMemo } from 'react';
import { useUserContext } from '@vitvio/user-context';
import { useMissionControlCalendarContext } from '../context/MissionControlCalendarContext';
import { SLOT_TOLERANCE_MINUTES } from '../../constants';
import { useORData } from './useORData';
import type {
  CapacitySlot,
  EventData,
  JobCardContent,
  ORColumnQueryNode,
  ORStatusType,
  RecordingAvailabilityEnum,
  SurgerySlot,
  TimelineSlot,
  TurnoverSlot,
} from '../types';

type JobCardNode = ORColumnQueryNode | NonNullable<ORColumnQueryNode['turnover']>;

function toJobCardData(node: Nullable<JobCardNode>): Nullable<JobCardContent> {
  if (!node) return null;
  const d = node.durations;
  const plannedStart = parseISOSafe(node.plannedStart) ?? null;
  const plannedStop = parseISOSafe(node.plannedStop) ?? null;
  const estimatedStartTime = parseISOSafe(node.estimatedStartTime) ?? null;
  const estimatedStopTime = parseISOSafe(node.estimatedStopTime) ?? null;
  const actualStartTimeParsed = parseISOSafe(node.actualStartTime) ?? null;
  const actualStopTimeParsed = parseISOSafe(node.actualStopTime) ?? null;
  const edges = node.events?.edges ?? [];
  const edge = edges.findLast((e) => e?.node != null);
  let lastEvent: Nullable<EventData> = null;
  if (edge?.node) {
    const name = edge.node.type?.name;
    const happenedAt = parseISOSafe(edge.node.happenedAt);
    lastEvent =
      typeof name === 'string' && name.trim() !== '' && happenedAt ? { eventTypeName: name, happenedAt } : null;
  }
  const status = node.status ?? null;
  const isStarted = status !== null && status !== 'SCHEDULED';
  const isComplete = status === 'COMPLETED';
  const actualStartTime = isStarted
    ? (actualStartTimeParsed ?? estimatedStartTime ?? plannedStart)
    : actualStartTimeParsed;
  const actualStopTime = isComplete ? (actualStopTimeParsed ?? estimatedStopTime ?? plannedStop) : actualStopTimeParsed;
  return nullsToUndefined({
    id: node.id,
    category: node.template?.category ?? null,
    procedureName: node.name,
    ...('patientMrn' in node && node.patientMrn !== undefined && { patientMrn: node.patientMrn }),
    ...('patientName' in node && node.patientName !== undefined && { patientName: node.patientName }),
    ...('surgeonName' in node && node.surgeonName !== undefined && { surgeonName: node.surgeonName }),
    ...('serviceName' in node && node.serviceName !== undefined && { serviceName: node.serviceName }),
    ...('location' in node && node.location?.name != null && { locationName: node.location.name }),
    status,
    scheduleStatus: node.scheduleStatus ?? null,
    plannedStart,
    plannedStop,
    estimatedStartTime,
    estimatedStopTime,
    actualStartTime,
    actualStopTime,
    estimateSource: node.estimateSource,
    durations: d
      ? {
          dynamicDurationMinutes: d.dynamicDurationMinutes,
          delayAfterMinutes: d.delayAfterMinutes,
          plannedDurationMinutes: d.plannedDurationMinutes,
        }
      : d,
    lastEvent,
  });
}

/** Content slots (surgery or turnover) with position in minutes; used inside hook. */
export type ContentSlot = {
  type: 'surgery' | 'turnover';
  startPosition: number;
  endPosition: number;
  data: JobCardContent;
};

type CapacityRange = { startPosition: number; endPosition: number; id: string };

interface UseORColumnProps {
  id: string;
  isDeployed?: boolean;
}

export type { CapacitySlot, SurgerySlot, TimelineSlot, TimelineSlotType, TurnoverSlot } from '../types';

export function useORColumn({ id, isDeployed = true }: UseORColumnProps) {
  const { hospitalTimezone, activeHospital } = useUserContext();
  const { workdayStartHour, workdayStopHour } = activeHospital;
  const { selectedDate } = useMissionControlCalendarContext();

  const { start: datetimeStart, end: datetimeEnd } = getDayBoundsInTimezone(selectedDate, hospitalTimezone);

  const data = useORData({ id, datetimeStart, datetimeEnd });

  // Get midnight in hospital timezone for accurate card positioning
  const midnight = getMidnightInTimezone(selectedDate, hospitalTimezone);

  const surgeries = useMemo(
    () =>
      (data?.operatingRoom?.surgeries?.edges ?? [])
        .filter((edge) => edge?.node?.status !== 'CANCELLED')
        .sort((a, b) => {
          const startTimeA = parseISOSafe(a?.node?.plannedStart);
          const startTimeB = parseISOSafe(b?.node?.plannedStart);
          return (startTimeA?.valueOf() ?? 0) - (startTimeB?.valueOf() ?? 0);
        }),
    [data?.operatingRoom?.surgeries?.edges],
  );

  const slots = useMemo((): TimelineSlot[] => {
    const contentSlots: ContentSlot[] = surgeries.flatMap((edge) => {
      const node = edge?.node;
      if (!node) return [];

      const cardData = toJobCardData(node);
      if (!cardData) return [];

      const calculatedStartTime = cardData.actualStartTime ?? cardData.estimatedStartTime ?? cardData.plannedStart;
      const startTimeForPosition = calculatedStartTime
        ? (toZonedTime(calculatedStartTime, hospitalTimezone) ?? dayjs())
        : dayjs();
      const startPosition = getDifference(midnight, startTimeForPosition, 'minutes');
      const duration = node.durations?.dynamicDurationMinutes ?? 0;
      const turnoverDuration = node.turnover?.durations?.dynamicDurationMinutes ?? 0;

      const surgerySlot: ContentSlot = {
        type: 'surgery',
        startPosition,
        endPosition: startPosition + duration,
        data: cardData,
      };

      const turnoverData = toJobCardData(node.turnover ?? null);

      const getTurnoverSlot = (data: JobCardContent): ContentSlot => {
        const turnoverCalculatedStart = data.actualStartTime ?? data.estimatedStartTime ?? data.plannedStart;
        const turnoverCalculatedStop = data.actualStopTime ?? data.estimatedStopTime ?? data.plannedStop;
        const surgeryEndPosition = startPosition + duration;
        const turnoverStartPosition =
          turnoverCalculatedStart != null
            ? getDifference(midnight, toZonedTime(turnoverCalculatedStart, hospitalTimezone) ?? dayjs(), 'minutes')
            : surgeryEndPosition;
        const turnoverEndPosition =
          turnoverCalculatedStop != null
            ? getDifference(midnight, toZonedTime(turnoverCalculatedStop, hospitalTimezone) ?? dayjs(), 'minutes')
            : surgeryEndPosition + turnoverDuration;

        return {
          type: 'turnover',
          startPosition: turnoverStartPosition,
          endPosition: turnoverEndPosition,
          data,
        };
      };

      return turnoverData ? [surgerySlot, getTurnoverSlot(turnoverData)] : [surgerySlot];
    });

    const sortedContentSlots = [...contentSlots]
      .sort((a, b) => a.startPosition - b.startPosition)
      .reduce<ContentSlot[]>((acc, slot, i, arr) => {
        const nextSlot = arr[i + 1];
        if (slot.type !== 'turnover') {
          if (nextSlot && slot.endPosition > nextSlot.startPosition) {
            return [...acc, { ...slot, endPosition: nextSlot.startPosition }];
          }
          return [...acc, slot];
        }
        const endPosition =
          nextSlot?.type === 'surgery' ? Math.min(slot.endPosition, nextSlot.startPosition) : slot.endPosition;
        const displayDurationMinutes = endPosition - slot.startPosition;
        const plannedMinutes = slot.data.durations?.plannedDurationMinutes ?? Infinity;
        const delayAfterMinutes = displayDurationMinutes < plannedMinutes ? 0 : slot.data.durations?.delayAfterMinutes;
        return [
          ...acc,
          {
            ...slot,
            endPosition,
            data: {
              ...slot.data,
              durations: {
                dynamicDurationMinutes: displayDurationMinutes,
                delayAfterMinutes,
                plannedDurationMinutes: slot.data.durations?.plannedDurationMinutes,
              },
            },
          },
        ];
      }, []);

    const workdayStartPosition = workdayStartHour * 60;
    const workdayStopPosition = workdayStopHour * 60;
    const contentSlotsOut: Array<SurgerySlot | TurnoverSlot> = sortedContentSlots.map((slot) => ({
      type: slot.type,
      id: slot.data.id,
      startPosition: slot.startPosition,
      endPosition: slot.endPosition,
      data: slot.data,
    }));
    const capacityRanges: CapacityRange[] =
      sortedContentSlots.length === 0
        ? [
            {
              startPosition: workdayStartPosition,
              endPosition: workdayStopPosition,
              id: `capacity-${workdayStartPosition}-${workdayStopPosition}`,
            },
          ]
        : [
            ...(sortedContentSlots[0].startPosition > workdayStartPosition
              ? [
                  {
                    startPosition: workdayStartPosition,
                    endPosition: sortedContentSlots[0].startPosition,
                    id: 'capacity-before-first',
                  },
                ]
              : []),
            ...sortedContentSlots.slice(0, -1).flatMap((slot, i) => {
              const next = sortedContentSlots[i + 1];
              return next.startPosition - slot.endPosition > SLOT_TOLERANCE_MINUTES
                ? [
                    {
                      startPosition: slot.endPosition,
                      endPosition: next.startPosition,
                      id: `capacity-gap-${slot.endPosition}`,
                    },
                  ]
                : [];
            }),
            ...(workdayStopPosition - sortedContentSlots[sortedContentSlots.length - 1].endPosition >
            SLOT_TOLERANCE_MINUTES
              ? [
                  {
                    startPosition: sortedContentSlots[sortedContentSlots.length - 1].endPosition,
                    endPosition: workdayStopPosition,
                    id: 'capacity-end-of-day',
                  },
                ]
              : []),
          ];
    const capacitySegments: CapacitySlot[] = capacityRanges.map(({ startPosition, endPosition, id }) => ({
      type: 'capacity' as const,
      id,
      startPosition,
      endPosition,
      data: {},
    }));

    return [...contentSlotsOut, ...capacitySegments].sort((a, b) => a.startPosition - b.startPosition);
  }, [surgeries, hospitalTimezone, midnight, workdayStartHour, workdayStopHour]);

  const orStatus = useMemo((): ORStatusType => {
    if (!isDeployed) return 'notDeployed';
    const recordingAvailability: RecordingAvailabilityEnum | null = data?.operatingRoom?.recordingAvailability ?? null;
    if (recordingAvailability === 'UNAVAILABLE') return 'unavailable';
    const relevant = surgeries.filter((e) => e?.node?.status === 'RUNNING');
    if (relevant.length === 0) return 'idle';
    if (relevant.some((e) => e?.node?.scheduleStatus === 'DELAYED')) return 'delays';
    if (relevant.some((e) => e?.node?.scheduleStatus === 'AHEAD')) return 'ahead';
    return 'onTime';
  }, [surgeries, data?.operatingRoom?.recordingAvailability, isDeployed]);

  return {
    data,
    operatingRoomName: data?.operatingRoom?.name ?? '',
    recordingAvailability: data?.operatingRoom?.recordingAvailability,
    orStatus,
    slots,
    isLoading: !data,
  };
}
